import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import {
  Home, Clock, Calendar, CheckCircle, ShieldCheck,
  ArrowRight, Heart, UserCheck, Star, Activity,
  Stethoscope, MapPin, Pill, Loader2
} from 'lucide-react';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import homeCareBg from '../../assets/images/home_nursing.webp';
import FeaturedCaregivers from './components/FeaturedCaregivers';
import apiClient from '../../api/api';
import { toCaregiverCard } from './components/caregiverCard';

// Custom "Not AI Generated" UI Components
const ProcessStep = ({ number, title, desc }) => (
  <div className="flex gap-6 group">
    <div className="flex-shrink-0 w-12 h-12 rounded-full border border-emerald-500/30 flex items-center justify-center text-emerald-400 font-mono text-lg font-bold group-hover:bg-emerald-500 group-hover:text-[#0b1120] transition-colors">
      {number}
    </div>
    <div>
      <h4 className="text-xl font-bold text-white mb-2">{title}</h4>
      <p className="text-slate-400">{desc}</p>
    </div>
  </div>
)

const HomeNursingPage = () => {
  const targetRef = useRef(null);
  const [staffData, setStaffData] = useState([]);
  const [availableCount, setAvailableCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { scrollYProgress } = useScroll({
    target: targetRef,
    offset: ["start end", "end start"],
  });

  const y = useTransform(scrollYProgress, [0, 1], [100, -100]);
  const opacity = useTransform(scrollYProgress, [0, 0.3, 0.8, 1], [0, 1, 1, 0]);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchWillingStaff();
  }, []);

  const fetchWillingStaff = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch both NURSE and CARETAKER staff who are willing to live in
      const [nursesResponse, caretakersResponse] = await Promise.all([
        apiClient.getStaffWillingToLiveIn({ role: 'NURSE', status: 'AVAILABLE', limit: 10, public_only: true }),
        apiClient.getStaffWillingToLiveIn({ role: 'CARETAKER', status: 'AVAILABLE', limit: 10, public_only: true })
      ]);

      const nurses = nursesResponse.data || [];
      const caretakers = caretakersResponse.data || [];

      // Transform API data to match expected format (real data only)
      const transformedStaff = [...nurses, ...caretakers].map(staff => {
        const isNurse = staff.role.includes('NURSE');
        return toCaregiverCard(staff, isNurse ? 'Nurse' : 'Caretaker', isNurse ? 'NURSE' : 'CARETAKER');
      });

      setStaffData(transformedStaff);
      setAvailableCount((nursesResponse.pagination?.total_count || 0) + (caretakersResponse.pagination?.total_count || 0));
    } catch (err) {
      console.error('Error fetching staff data:', err);
      setError('Failed to load staff data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 selection:bg-emerald-100 selection:text-emerald-900">
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center pt-24 pb-12 lg:pt-32 lg:pb-20 overflow-hidden">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">

            {/* Text Content */}
            <div className="order-2 lg:order-1 relative z-20">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-bold uppercase tracking-wider mb-6">
                  <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  Clinical • Skilled • Recovery
                </div>

                <h1 className="text-4xl xs:text-5xl sm:text-6xl md:text-7xl font-bold tracking-tighter text-slate-900 mb-6 leading-[1.0] md:leading-[0.9]">
                  Professional <br />
                  <span className="text-emerald-500">
                    Home Nursing.
                  </span>
                </h1>

                <p className="text-lg md:text-xl text-slate-600 mb-8 max-w-lg leading-relaxed">
                  Qualified nurses delivering expert medical care, wound management, and post-surgical recovery in the comfort of your home.
                </p>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/services/home-nursing/book" className="px-8 py-4 bg-emerald-500 text-white rounded-full font-bold text-lg hover:bg-emerald-600 transition-all shadow-lg shadow-emerald-500/20 flex items-center justify-center gap-2">
                    Request Care <ArrowRight className="w-5 h-5" />
                  </Link>

                  {availableCount > 0 && (
                    <div className="flex items-center gap-3 px-6 py-4 bg-white border border-slate-200 rounded-full shadow-sm">
                      <span className="text-lg font-bold text-slate-900">{availableCount}</span>
                      <span className="text-sm font-medium text-slate-600">Available Nurses</span>
                    </div>
                  )}
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />)}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Sticky Scroll Section - "The VCare Standard" */}
      <section className="py-16 lg:py-32 bg-white relative">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-24">
            <div className="lg:sticky lg:top-32 h-fit">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 text-slate-900">Not an Agency. <br />A Medical Partner.</h2>
              <p className="text-lg md:text-xl text-slate-600 leading-relaxed mb-8">
                Most agencies just send a person. We send a managed care protocol.
                Every nurse is connected to our Command OS, ensuring vitals are recorded,
                medicines are tracked, and doctors are kept in the loop.
              </p>

            </div>

            <div className="space-y-12 lg:space-y-32 lg:pt-12">
              <div className="group">
                <div className="h-48 sm:h-64 bg-slate-50 rounded-2xl mb-6 flex items-center justify-center border border-slate-200 group-hover:border-emerald-500/30 transition-colors">
                  <Stethoscope className="w-24 h-24 text-slate-400 group-hover:text-emerald-500 transition-colors" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">Clinical Expertise</h3>
                <p className="text-slate-600 text-lg">ICU trained staff capable of handling ventilators, tracheostomy, and post-stroke recovery.</p>
              </div>

              <div className="group">
                <div className="h-48 sm:h-64 bg-slate-50 rounded-2xl mb-6 flex items-center justify-center border border-slate-200 group-hover:border-emerald-500/30 transition-colors">
                  <ShieldCheck className="w-24 h-24 text-slate-400 group-hover:text-emerald-500 transition-colors" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">Background Verified</h3>
                <p className="text-slate-600 text-lg">Police verification, aadhaar validation, and previous employer checks are mandatory.</p>
              </div>


            </div>
          </div>
        </div>
      </section>

      {/* How it works - Horizontal Steps */}


      {/* Featured Staff Section - Willing to Live In */}
      <section className="py-24 bg-white">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4 text-center">
              Available for Live-In Care
            </h2>
            <p className="text-slate-600 text-lg mb-8 text-center max-w-3xl">
              Professional nurses and caretakers ready to provide full-time home care services.
            </p>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="w-8 h-8 text-emerald-500 animate-spin mb-4" />
              <p className="text-slate-600">Loading available staff...</p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="text-center py-16">
              <p className="text-red-500 mb-4">{error}</p>
              <button
                onClick={fetchWillingStaff}
                className="px-6 py-2 bg-emerald-500 text-white rounded-full font-medium hover:bg-emerald-600 transition-colors"
              >
                Try Again
              </button>
            </div>
          )}

          {/* Staff Data */}
          {!loading && !error && (
            <FeaturedCaregivers
              workers={staffData}
              colorTheme="emerald"
            />
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomeNursingPage;
